import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Radio, MapPin, RefreshCw, ShieldAlert, Wrench, Search, X, Car,
  Fuel, Gauge, Clock, Bell, ClipboardList, Trash2, Plus, CheckCircle2,
  AlertTriangle, Eye, Save, Signal, SignalZero,
} from "lucide-react";
import { api } from "../../lib/api";

/* --------------------------- Leaflet loader --------------------------- */
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
let leafletPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet"; link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const s = document.createElement("script");
    s.src = LEAFLET_JS;
    s.onload  = () => resolve(window.L);
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return leafletPromise;
}

/* --------------------------- helpers --------------------------- */
const STATUS_STYLE = {
  driving:      { tone: "text-lime-300 border-lime-400/40 bg-lime-400/[0.07]",   label: "DRIVING" },
  parked:       { tone: "text-cyan   border-cyan/40       bg-cyan/[0.07]",       label: "PARKED" },
  rented:       { tone: "text-amber-300 border-amber-400/40 bg-amber-400/[0.07]",label: "RENTED" },
  available:    { tone: "text-white/70 border-white/20    bg-white/[0.05]",       label: "AVAILABLE" },
  offline:      { tone: "text-white/40 border-white/10    bg-white/[0.02]",       label: "OFFLINE" },
  maintenance:  { tone: "text-orange-300 border-orange-400/40 bg-orange-400/[0.07]",label: "MAINT." },
  overdue:      { tone: "text-red-300 border-red-400/50   bg-red-400/[0.07]",    label: "OVERDUE" },
};
const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.available;
  return (
    <span className={`inline-flex items-center label-caps text-[10px] px-2 py-1 rounded-full border ${s.tone}`}>
      {s.label}
    </span>
  );
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return "—"; } };

/* --------------------------- Summary strip --------------------------- */
const SummaryTile = ({ label, value, tone = "border-white/10 text-white/70" }) => (
  <div
    data-testid={`fc-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`rounded-2xl border p-4 bg-black/30 ${tone}`}
  >
    <div className="label-caps text-[10px] opacity-70">{label}</div>
    <div className="text-white text-2xl font-serif mt-1">{value ?? 0}</div>
  </div>
);

/* --------------------------- Map --------------------------- */
function FleetMap({ vehicles, geofences, onSelect, connected }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const layerRef     = useRef(null);
  const geoLayerRef  = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || !containerRef.current || mapRef.current) return;
      const map = L.map(containerRef.current, {
        zoomControl: true, attributionControl: false,
      }).setView([40.7128, -74.0060], 9);
      L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        { subdomains: "abcd", maxZoom: 19 }
      ).addTo(map);
      mapRef.current    = map;
      layerRef.current  = L.layerGroup().addTo(map);
      geoLayerRef.current = L.layerGroup().addTo(map);
      // trigger initial draw
      setTimeout(() => map.invalidateSize(), 100);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const L = window.L;
    if (!L || !layerRef.current) return;
    layerRef.current.clearLayers();
    (vehicles || []).forEach((v) => {
      if (!v.location?.lat || !v.location?.lng) return;
      const color = v.status === "driving"     ? "#84cc16"
                  : v.status === "overdue"     ? "#f87171"
                  : v.status === "rented"      ? "#fbbf24"
                  : v.status === "maintenance" ? "#fb923c"
                  : "#67e8f9";
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:14px;height:14px;border-radius:9999px;background:${color};box-shadow:0 0 0 3px rgba(0,0,0,0.55),0 0 12px ${color};border:1.5px solid rgba(255,255,255,0.7);"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7],
      });
      L.marker([v.location.lat, v.location.lng], { icon })
        .addTo(layerRef.current)
        .on("click", () => onSelect?.(v));
    });
  }, [vehicles, onSelect]);

  useEffect(() => {
    const L = window.L;
    if (!L || !geoLayerRef.current) return;
    geoLayerRef.current.clearLayers();
    (geofences || []).forEach((g) => {
      if (g.type === "circle" && g.center_lat && g.center_lng && g.radius_km) {
        L.circle([g.center_lat, g.center_lng], {
          radius: g.radius_km * 1000, color: "#22d3ee", weight: 1.5,
          fillColor: "#22d3ee", fillOpacity: 0.06,
        }).addTo(geoLayerRef.current).bindTooltip(g.name);
      }
    });
  }, [geofences]);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black/40" style={{ height: 420 }}>
      <div ref={containerRef} data-testid="fc-map" style={{ width: "100%", height: "100%" }} />
      {!connected && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto px-4 py-3 rounded-xl border border-amber-400/40 bg-black/80 text-center max-w-md">
            <div className="label-caps text-amber-300 text-[10px] mb-1 inline-flex items-center gap-1">
              <SignalZero size={12} /> Telematics Connection Required
            </div>
            <div className="text-white text-sm">
              Live vehicle positions will appear once the Kia telematics API is connected.
            </div>
            <div className="text-white/50 text-[11px] mt-1">
              Configure <code className="text-cyan">KIA_API_BASE_URL</code> and <code className="text-cyan">KIA_API_KEY</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------- Vehicle card --------------------------- */
function VehicleCard({ v, onOpen }) {
  return (
    <button
      type="button"
      data-testid={`fc-vehicle-${v.id}`}
      onClick={() => onOpen(v)}
      className="text-left rounded-2xl border border-white/10 bg-black/30 p-4 hover:border-white/25 transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-white text-sm truncate font-medium">{v.name}</div>
          <div className="text-white/50 text-[11px] truncate">
            {v.year || "—"} · {v.color || "—"} · Unit {v.unit_number || "—"}
          </div>
          <div className="text-white/40 text-[10px] mt-1 font-mono truncate">VIN {v.vin || "—"}</div>
        </div>
        <StatusPill status={v.status} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-white/60">
        <div className="inline-flex items-center gap-1"><Gauge size={11} /> {v.odometer ?? "—"}</div>
        <div className="inline-flex items-center gap-1"><Fuel size={11} /> {v.fuel_level != null ? `${v.fuel_level}%` : "—"}</div>
        <div className="inline-flex items-center gap-1"><MapPin size={11} /> {v.location ? "Live" : "—"}</div>
      </div>
      {v.reservation && (
        <div className="mt-2 pt-2 border-t border-white/8 text-[11px] text-white/70">
          <span className="text-amber-300">{v.reservation.full_name}</span>
          {" · "}
          <span className="font-mono text-white/50">{v.reservation.reservation_id || v.reservation.id}</span>
        </div>
      )}
    </button>
  );
}

/* --------------------------- Detail Modal --------------------------- */
function VehicleDetailModal({ vehicle, onClose, onSaved }) {
  const [detail, setDetail]         = useState(null);
  const [notes, setNotes]           = useState("");
  const [maint, setMaint]           = useState({});
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    if (!vehicle) return;
    api.get(`/admin/fleet-command/vehicles/${vehicle.id}`)
      .then((r) => {
        setDetail(r.data);
        setNotes(r.data.admin_notes || "");
        setMaint(r.data.maintenance || {});
      })
      .catch(() => toast.error("Couldn't load vehicle detail"));
  }, [vehicle]);

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/fleet-command/vehicles/${vehicle.id}/notes`, { notes });
      toast.success("Notes saved");
      onSaved?.();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const saveMaint = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/fleet-command/maintenance/${vehicle.id}`, maint);
      toast.success("Maintenance saved");
      onSaved?.();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!vehicle) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto" data-testid="fc-detail-modal">
      <div className="w-full max-w-3xl bg-neutral-950 border border-white/10 rounded-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-white font-serif text-lg truncate">{vehicle.name}</div>
            <div className="text-white/50 text-[11px] truncate">
              VIN {vehicle.vin || "—"} · Unit {vehicle.unit_number || "—"}
            </div>
          </div>
          <button data-testid="fc-detail-close" onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-white/60"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryTile label="Status"   value={<StatusPill status={vehicle.status} />} />
            <SummaryTile label="Odometer" value={detail?.odometer ?? "—"} />
            <SummaryTile label="Fuel"     value={detail?.fuel_level != null ? `${detail.fuel_level}%` : "—"} />
            <SummaryTile label="Last Seen"value={detail?.last_seen_at ? fmt(detail.last_seen_at) : "—"} />
          </div>

          {detail?.reservation && (
            <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.05] p-4">
              <div className="label-caps text-amber-300 text-[10px] mb-1">Current Rental</div>
              <div className="grid grid-cols-2 gap-2 text-sm text-white/80">
                <div><span className="text-white/40">Renter</span><br />{detail.reservation.full_name || "—"}</div>
                <div><span className="text-white/40">Reservation</span><br /><span className="font-mono">{detail.reservation.reservation_id || detail.reservation.id}</span></div>
                <div><span className="text-white/40">Pickup</span><br />{fmt(detail.reservation.start_at)}</div>
                <div><span className="text-white/40">Return</span><br />{fmt(detail.reservation.end_at)}</div>
                <div><span className="text-white/40">Contract</span><br />{detail.reservation.pandadoc_status || (detail.reservation.contract_signed_at ? "signed" : "—")}</div>
                <div><span className="text-white/40">Balance</span><br />
                  {`$${((detail.reservation.amount || 0) - (detail.reservation.balance_paid_amount || 0)).toFixed(2)}`}
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="label-caps text-cyan text-[10px] mb-2">Admin Notes (internal only)</div>
            <textarea
              data-testid="fc-notes-input"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-xl bg-black/50 border border-white/10 text-white text-sm p-3"
              placeholder="Notes are visible only to admin staff…"
            />
            <div className="mt-2 flex justify-end">
              <button data-testid="fc-notes-save" onClick={saveNotes} disabled={saving}
                className="px-3 py-1.5 rounded-full text-xs border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1">
                <Save size={11} /> Save Notes
              </button>
            </div>
          </div>

          <div>
            <div className="label-caps text-cyan text-[10px] mb-2">Maintenance</div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["oil_interval_km",   "Oil interval (km)"],
                ["oil_next_odometer", "Oil due at (odometer)"],
                ["tire_interval_km",  "Tire interval (km)"],
                ["tire_next_odometer","Tire due at (odometer)"],
                ["inspection_next_at","Next inspection (date)"],
                ["registration_expires_at","Registration expires"],
                ["last_odometer",     "Last known odometer"],
              ].map(([k, label]) => (
                <label key={k} className="text-[11px] text-white/60">
                  {label}
                  <input
                    data-testid={`fc-maint-${k}`}
                    value={maint[k] ?? ""}
                    onChange={(e) => setMaint((m) => ({ ...m, [k]: e.target.value }))}
                    className="w-full mt-1 rounded-lg bg-black/50 border border-white/10 text-white text-sm px-2 py-1.5"
                    type={k.includes("_at") ? "date" : "text"}
                  />
                </label>
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <button data-testid="fc-maint-save" onClick={saveMaint} disabled={saving}
                className="px-3 py-1.5 rounded-full text-xs border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1">
                <Save size={11} /> Save Maintenance
              </button>
            </div>
          </div>

          <div>
            <div className="label-caps text-cyan text-[10px] mb-2">Recent Rental History</div>
            {(detail?.rental_history || []).length === 0 ? (
              <div className="text-white/40 text-sm">No history.</div>
            ) : (
              <ol className="divide-y divide-white/8 text-sm">
                {detail.rental_history.map((h) => (
                  <li key={h.id} className="py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white truncate">{h.full_name || "—"}</div>
                      <div className="text-white/40 text-[11px] truncate">
                        {fmt(h.start_at)} → {fmt(h.end_at)} · <span className="font-mono">{h.reservation_id || h.id}</span>
                      </div>
                    </div>
                    <span className="label-caps text-[10px] px-2 py-1 rounded-full border border-white/15 text-white/60">
                      {h.status}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Alert Center --------------------------- */
function AlertCenter({ refreshKey, onChanged }) {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/fleet-command/alerts${filter ? `?status=${filter}` : ""}`)
      .then((r) => setAlerts(r.data.alerts || []))
      .catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/admin/fleet-command/alerts/${a.id}`, { status });
      load(); onChanged?.();
    } catch { toast.error("Update failed"); }
  };

  const sweep = async () => {
    try {
      const r = await api.post("/admin/fleet-command/sweep");
      toast.success(`Sweep complete: ${r.data.created} new alert(s)`);
      load(); onChanged?.();
    } catch { toast.error("Sweep failed"); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="fc-alert-center">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="inline-flex items-center gap-2 text-white">
          <Bell size={14} className="text-amber-300" />
          <span className="font-serif">Alert Center</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="fc-alert-filter"
            value={filter} onChange={(e) => setFilter(e.target.value)}
            className="bg-black/50 border border-white/10 rounded-full text-xs text-white/70 px-3 py-1.5"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="resolved">Resolved</option>
          </select>
          <button data-testid="fc-alert-sweep" onClick={sweep} className="px-3 py-1.5 rounded-full text-xs border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1">
            <RefreshCw size={11} /> Run sweep
          </button>
        </div>
      </div>
      {alerts.length === 0 ? (
        <div className="text-white/40 text-sm py-4 text-center">No alerts.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 label-caps text-[10px]">
                <th className="text-left py-2">Vehicle</th>
                <th className="text-left py-2">Time</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Reservation</th>
                <th className="text-left py-2">Customer</th>
                <th className="text-left py-2">Status</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id} data-testid={`fc-alert-${a.id}`} className="border-t border-white/8">
                  <td className="py-2 text-white/80">{a.vehicle_name || "—"}</td>
                  <td className="py-2 text-white/50 text-[11px]">{fmt(a.created_at)}</td>
                  <td className="py-2 text-amber-300 label-caps text-[10px]">{a.kind}</td>
                  <td className="py-2 font-mono text-white/60 text-[11px]">{a.reservation_id || "—"}</td>
                  <td className="py-2 text-white/70">{a.customer || "—"}</td>
                  <td className="py-2">
                    <span className={`label-caps text-[10px] px-2 py-0.5 rounded-full border ${
                      a.status === "new" ? "text-red-300 border-red-400/40"
                      : a.status === "reviewing" ? "text-amber-300 border-amber-400/40"
                      : "text-white/40 border-white/10"
                    }`}>{a.status}</span>
                  </td>
                  <td className="py-2 text-right">
                    {a.status !== "reviewing" && (
                      <button onClick={() => setStatus(a, "reviewing")} data-testid={`fc-alert-review-${a.id}`}
                        className="px-2 py-0.5 rounded-full text-[10px] text-amber-300 border border-amber-400/40 mr-1">Review</button>
                    )}
                    {a.status !== "resolved" && (
                      <button onClick={() => setStatus(a, "resolved")} data-testid={`fc-alert-resolve-${a.id}`}
                        className="px-2 py-0.5 rounded-full text-[10px] text-lime-300 border border-lime-400/40">Resolve</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* --------------------------- Geofence Panel --------------------------- */
function GeofencePanel({ geofences, onChanged }) {
  const [form, setForm] = useState({ name: "", center_lat: "", center_lng: "", radius_km: "" });
  const create = async () => {
    if (!form.name || !form.center_lat || !form.center_lng || !form.radius_km) {
      toast.error("All fields required"); return;
    }
    try {
      await api.post("/admin/fleet-command/geofences", {
        name: form.name, type: "circle",
        center_lat: parseFloat(form.center_lat),
        center_lng: parseFloat(form.center_lng),
        radius_km:  parseFloat(form.radius_km),
      });
      toast.success("Zone created");
      setForm({ name: "", center_lat: "", center_lng: "", radius_km: "" });
      onChanged?.();
    } catch { toast.error("Create failed"); }
  };
  const remove = async (g) => {
    try {
      await api.delete(`/admin/fleet-command/geofences/${g.id}`);
      toast.success("Zone removed");
      onChanged?.();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="fc-geofence-panel">
      <div className="inline-flex items-center gap-2 text-white mb-3">
        <ShieldAlert size={14} className="text-cyan" />
        <span className="font-serif">Geofences</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-3">
        <input data-testid="fc-geo-name" placeholder="Zone name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="rounded-lg bg-black/50 border border-white/10 text-white text-sm px-2 py-1.5 sm:col-span-2" />
        <input data-testid="fc-geo-lat" placeholder="Lat"  value={form.center_lat} onChange={(e) => setForm({ ...form, center_lat: e.target.value })}
          className="rounded-lg bg-black/50 border border-white/10 text-white text-sm px-2 py-1.5" />
        <input data-testid="fc-geo-lng" placeholder="Lng"  value={form.center_lng} onChange={(e) => setForm({ ...form, center_lng: e.target.value })}
          className="rounded-lg bg-black/50 border border-white/10 text-white text-sm px-2 py-1.5" />
        <div className="flex gap-2">
          <input data-testid="fc-geo-radius" placeholder="Radius km" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: e.target.value })}
            className="rounded-lg bg-black/50 border border-white/10 text-white text-sm px-2 py-1.5 flex-1" />
          <button data-testid="fc-geo-create" onClick={create} className="px-3 py-1.5 rounded-full text-xs border border-cyan/40 text-cyan hover:bg-cyan/10 inline-flex items-center gap-1">
            <Plus size={11} /> Add
          </button>
        </div>
      </div>
      {geofences.length === 0 ? (
        <div className="text-white/40 text-sm py-3 text-center">No zones configured.</div>
      ) : (
        <ol className="divide-y divide-white/8">
          {geofences.map((g) => (
            <li key={g.id} data-testid={`fc-geo-${g.id}`} className="py-2 flex items-center justify-between gap-3 text-sm">
              <div>
                <div className="text-white">{g.name}</div>
                <div className="text-white/40 text-[11px]">
                  {g.center_lat?.toFixed?.(4)}, {g.center_lng?.toFixed?.(4)} · {g.radius_km} km
                </div>
              </div>
              <button data-testid={`fc-geo-delete-${g.id}`} onClick={() => remove(g)} className="p-1.5 rounded-full text-red-300 hover:bg-red-400/10">
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* --------------------------- Root tab --------------------------- */
export default function FleetCommandTab() {
  const [connStatus, setConnStatus] = useState(null);
  const [summary,   setSummary]     = useState(null);
  const [vehicles,  setVehicles]    = useState([]);
  const [geofences, setGeofences]   = useState([]);
  const [filter,    setFilter]      = useState("all");
  const [q,         setQ]           = useState("");
  const [selected,  setSelected]    = useState(null);
  const [alertBump, setAlertBump]   = useState(0);

  const loadAll = useCallback(async () => {
    try {
      const [s, sum, v, g] = await Promise.all([
        api.get("/admin/fleet-command/status"),
        api.get("/admin/fleet-command/summary"),
        api.get(`/admin/fleet-command/vehicles?filter=${filter}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get("/admin/fleet-command/geofences"),
      ]);
      setConnStatus(s.data);
      setSummary(sum.data);
      setVehicles(v.data.vehicles || []);
      setGeofences(g.data.geofences || []);
    } catch { toast.error("Couldn't load Fleet Command"); }
  }, [filter, q]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const counts = summary?.counts || {};
  const filters = useMemo(() => ([
    { key: "all",             label: "All",           count: counts.total },
    { key: "driving",         label: "Driving",       count: counts.driving },
    { key: "parked",          label: "Parked",        count: counts.parked },
    { key: "rented",          label: "Rented",        count: counts.rented },
    { key: "available",       label: "Available",     count: counts.available },
    { key: "offline",         label: "Offline",       count: counts.offline },
    { key: "overdue",         label: "Overdue",       count: counts.overdue },
    { key: "maintenance_due", label: "Maint. Due",    count: counts.maintenance_due },
  ]), [counts]);

  return (
    <div data-testid="bo-fleet-command-root" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps text-cyan text-[11px] mb-1 inline-flex items-center gap-1">
            {connStatus?.connected
              ? <><Signal size={11} /> Live</>
              : <><SignalZero size={11} /> Offline</>}
          </div>
          <h2 className="font-serif text-2xl text-white flex items-center gap-2">
            <Radio className="text-cyan" size={20} /> Kia Fleet Command
          </h2>
          <div className="text-white/50 text-xs">
            Admin-only telematics & fleet operations · Kia rental vehicles
          </div>
        </div>
        <button data-testid="fc-refresh" onClick={loadAll} className="px-3 py-2 rounded-full border border-white/15 text-xs label-caps text-white/70 hover:text-white hover:border-white/30 inline-flex items-center gap-1.5">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Connection banner */}
      {connStatus && !connStatus.connected && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-400/[0.06] p-4" data-testid="fc-connection-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-300 shrink-0 mt-0.5" size={16} />
            <div className="text-sm text-white/85">
              <div className="font-medium text-amber-200">Telematics Connection Required</div>
              <div className="text-white/70 mt-0.5">
                {connStatus.message} Once configured, live positions, fuel/range, odometer and
                driving state will populate automatically. No personal Kia Connect credentials
                are ever stored — only the API keys below.
              </div>
              <div className="text-white/50 text-[11px] mt-2 font-mono">
                Required env vars: {connStatus.required_credentials.join(" · ")}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
        <SummaryTile label="Total"        value={counts.total} />
        <SummaryTile label="Rented"       value={counts.rented}       tone="border-amber-400/30 text-amber-300" />
        <SummaryTile label="Available"    value={counts.available}    tone="border-white/15 text-white/80" />
        <SummaryTile label="Parked"       value={counts.parked}       tone="border-cyan/30 text-cyan" />
        <SummaryTile label="Driving"      value={counts.driving}      tone="border-lime-400/30 text-lime-300" />
        <SummaryTile label="Offline"      value={counts.offline}      tone="border-white/10 text-white/50" />
        <SummaryTile label="Maint. Due"   value={counts.maintenance_due} tone="border-orange-400/30 text-orange-300" />
        <SummaryTile label="Overdue"      value={counts.overdue}      tone="border-red-400/40 text-red-300" />
        <SummaryTile label="Active Alerts" value={summary?.active_alerts ?? 0} tone="border-red-400/40 text-red-300" />
      </div>

      {/* Map */}
      <FleetMap
        vehicles={vehicles}
        geofences={geofences}
        onSelect={setSelected}
        connected={!!connStatus?.connected}
      />

      {/* Filter bar */}
      <div className="rounded-2xl border border-white/10 bg-black/30 p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={13} />
            <input
              data-testid="fc-search"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search VIN, name, unit…"
              className="w-full pl-8 pr-3 py-2 rounded-full bg-black/50 border border-white/10 text-sm text-white"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {filters.map((f) => (
              <button
                key={f.key}
                data-testid={`fc-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs label-caps border transition ${
                  filter === f.key
                    ? "border-cyan text-cyan bg-cyan/10"
                    : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                }`}
              >
                {f.label}{f.count != null ? ` (${f.count})` : ""}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Vehicle grid */}
      {vehicles.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-black/30 p-10 text-center text-white/50">
          <Car size={22} className="mx-auto text-white/30 mb-2" />
          No Kia vehicles match this view.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fc-vehicle-grid">
          {vehicles.map((v) => (<VehicleCard key={v.id} v={v} onOpen={setSelected} />))}
        </div>
      )}

      {/* Alerts + Geofences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AlertCenter refreshKey={alertBump} onChanged={() => { setAlertBump(x => x + 1); loadAll(); }} />
        <GeofencePanel geofences={geofences} onChanged={loadAll} />
      </div>

      {/* Detail modal */}
      {selected && (
        <VehicleDetailModal
          vehicle={selected}
          onClose={() => setSelected(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}
